import { useEffect, useState } from 'react';
import { Button, ConfigProvider, DatePicker, Drawer, Input, Select } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs, { type Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import type { Receipt } from '@deposits/shared';
import { Money } from '../../components/Money';
import { summarize } from './SummaryBar';
import { fetchStaffMembers, refreshStaffMembers } from '../../api/staffMembers';
import { useCreateDeposit } from '../../hooks/useDeposits';
import { useLocationContext } from '../../auth/LocationContext';
import { useProjectContext } from '../../auth/ProjectContext';
import { todayIsrael } from '../../utils/dates';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called once the deposit is created. The parent should clear selection,
      switch to Tab B, and open DepositPdfDrawer with the new id. */
  onCreated: (depositId: number, depositNumber: number) => void;
  selected: Receipt[];
}

export function NewDepositDrawer({ open, onClose, onCreated, selected }: Props) {
  const { selectedLocationId, selectedLocation } = useLocationContext();
  const { selectedProjectId, selectedProject } = useProjectContext();
  const t = summarize(selected);

  const [depositDate, setDepositDate] = useState<Dayjs>(() => dayjs(todayIsrael()));
  const [depositorArboxUsersBoxesId, setDepositorArboxUsersBoxesId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  // Reset form fields whenever the drawer is reopened.
  useEffect(() => {
    if (open) {
      setDepositDate(dayjs(todayIsrael()));
      setDepositorArboxUsersBoxesId(null);
      setNotes('');
    }
  }, [open]);

  const queryClient = useQueryClient();
  const staffQuery = useQuery({
    queryKey: ['staff-members'],
    queryFn: fetchStaffMembers,
    staleTime: 5 * 60_000,
  });

  // On-demand re-sync from Arbox. The server route fetches + UPSERTs and
  // returns the fresh list; we just write it into the react-query cache so
  // the Select reflects it immediately.
  const refreshMutation = useMutation({
    mutationFn: refreshStaffMembers,
    onSuccess: (data) => {
      queryClient.setQueryData(['staff-members'], data);
      toast.success('הרשימה עודכנה');
    },
    onError: (err: Error) =>
      toast.error(err.message || 'שגיאה ברענון אנשי הצוות'),
  });

  const createMutation = useCreateDeposit();

  const submit = () => {
    if (selected.length === 0 || selectedLocationId == null) return;

    createMutation.mutate(
      {
        location_id: selectedLocationId,
        project_id: selectedProjectId ?? undefined,
        deposit_date: depositDate.format('YYYY-MM-DD'),
        depositor_arbox_users_boxes_id: depositorArboxUsersBoxesId,
        notes: notes.trim() || null,
        receipt_refs: selected.map((r) => ({ external_receipt_id: r.external_receipt_id })),
      },
      {
        onSuccess: (created) => {
          toast.success(`הפקדה מספר ${created.deposit_number} נוצרה`);
          if (!created.pdf_url) {
            toast.error('ההפקדה נוצרה אך נכשלה הפקת ה-PDF — נסה להדפיס מטאב הדוח');
          }
          // Parent opens DepositPdfDrawer with this id (in-app PDF view).
          onCreated(created.id, created.deposit_number);
        },
        onError: (err) => {
          // Close the drawer on dedupe-fail so the user sees the refreshed
          // Tab A pool (the hook also invalidates ['receipts-pending']).
          // Otherwise they'd be staring at a stale selection.
          const isStaleSelection = err.message?.includes('כבר הופקדו');
          toast.error(err.message || 'שגיאה ביצירת ההפקדה');
          if (isStaleSelection) onClose();
        },
      },
    );
  };

  return (
    <Drawer
      title={<span className="text-heading-3-bold text-main-text">הפקדה חדשה</span>}
      placement="left" // antd flips to right in RTL
      open={open}
      onClose={onClose}
      width={560}
      closable={false}
      styles={{ header: { borderBottom: 'none' } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        {/* Selection summary panel */}
        <div className="bg-blue-bg border border-border rounded p-4 mb-6">
          <div className="font-semibold mb-3">סיכום הבחירה</div>
          <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-base">
            <SummaryRow label="סניף">
              <span>{selectedLocation?.name ?? '—'}</span>
            </SummaryRow>
            {selectedProject && (
              <SummaryRow label="פרויקט">
                <span>{selectedProject.name}</span>
              </SummaryRow>
            )}
            <SummaryRow label="כמות צ'קים">
              <span dir="ltr">{t.checkCount}</span>
            </SummaryRow>
            <SummaryRow label="סה&quot;כ צ'קים">
              <Money amount={t.checksTotal} />
            </SummaryRow>
            <SummaryRow label='סה"כ מזומן'>
              <Money amount={t.cashTotal} />
            </SummaryRow>
            <SummaryRow label='סה"כ להפקדה' bold>
              <Money amount={t.grandTotal} />
            </SummaryRow>
          </div>
        </div>

        {/* Form fields */}
        <div className="flex flex-col gap-4">
          <Field label="תאריך הפקדה" required>
            <DatePicker
              value={depositDate}
              onChange={(d) => d && setDepositDate(d)}
              format="DD/MM/YYYY"
              allowClear={false}
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="איש צוות מפקיד">
            <Select
              value={depositorArboxUsersBoxesId ?? undefined}
              onChange={(v) => setDepositorArboxUsersBoxesId(v ?? null)}
              placeholder="בחר איש צוות (לא חובה)"
              allowClear
              showSearch
              optionFilterProp="label"
              loading={staffQuery.isLoading}
              // staff_members is already sorted alphabetically server-side;
              // resorting here is defensive in case react-query merges from
              // a different source.
              options={(staffQuery.data ?? [])
                .slice()
                .sort((a, b) => a.display_name.localeCompare(b.display_name, 'he'))
                .map((u) => ({
                  value: u.arbox_users_boxes_id,
                  label: u.display_name,
                }))}
              style={{ width: '100%' }}
            />
            <button
              type="button"
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="mt-1 inline-flex items-center gap-1.5 text-small text-secondary-text hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon
                icon={faRotateRight}
                style={{ fontSize: 11 }}
                spin={refreshMutation.isPending}
              />
              <span>איש צוות חסר? לחץ פה לרענון</span>
            </button>
          </Field>

          <Field label="הערות">
            <Input.TextArea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              showCount
              rows={3}
              placeholder="פירוט אופציונלי..."
            />
          </Field>

          <div className="flex justify-end gap-3 mt-4">
            <Button onClick={onClose}>ביטול</Button>
            <Button
              type="primary"
              onClick={submit}
              loading={createMutation.isPending}
              disabled={selected.length === 0}
            >
              הפק
            </Button>
          </div>
        </div>
      </ConfigProvider>
    </Drawer>
  );
}

function SummaryRow({
  label,
  children,
  bold,
}: {
  label: string;
  children: React.ReactNode;
  bold?: boolean;
}) {
  return (
    <>
      <div className="text-secondary-text">{label}</div>
      <div className={`text-end ${bold ? 'font-semibold text-heading-3' : ''}`}>{children}</div>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-small text-main-text mb-1">
        {label}
        {required && <span className="text-error-red"> *</span>}
      </div>
      {children}
    </div>
  );
}
